import graphqlClient from '../../common/api/graphql';
import {
  GetBotSessionsInBillingCycle,
  GetOrganizationBotsLight,
  GetOrganizationCredits,
  GetUserBotRole,
  GetUserRole,
  GetUsersOrganizationUsers,
} from '../../common/queries/queries';
import { toHasuraMember } from './orgMembersApi';
import { getAll, getData, orgPath } from './restApi';

// Ola 2: 'true' reads the user, org members, credits, bots and role, and the billing-cycle usage from REST.
export const isUserStoreRest = (): boolean => process.env.NEXT_PUBLIC_REST_USER_STORE === 'true';

type Row = any;

async function hasura(query: any, variables: Record<string, unknown>): Promise<Row> {
  const { data } = await graphqlClient.query({ query, variables, fetchPolicy: 'no-cache' });
  return data;
}

// users_by_pk of the signed-in user: admin, name, is_available.
export async function fetchUserProfile(userId: string): Promise<Row | null> {
  if (isUserStoreRest()) {
    // Login depends on it: right after signup /me may 404 on the replica, so fall back like before.
    const me = await getData('me').catch(() => null);
    if (me) return { admin: me.admin, name: me.name, is_available: me.is_available };
  }
  return (await hasura(GetUserRole, { user_id: userId }))?.users_by_pk ?? null;
}

// users_organizations rows of the org; the store drops inactive ones (active === false) itself.
export async function fetchOrganizationUserRows(orgId: string): Promise<Row[]> {
  if (isUserStoreRest()) return (await getAll(orgPath(orgId, '/members'))).map(toHasuraMember);
  return (await hasura(GetUsersOrganizationUsers, { organization_id: orgId }))?.users_organizations || [];
}

// GetOrganizationCredits shape: organization_credits and organization_autorecharge as lists.
export async function fetchOrganizationCreditRows(orgId: string): Promise<Row> {
  if (isUserStoreRest()) {
    const { credits, autorecharge } = await getData(orgPath(orgId, '/credits'));
    return {
      organization_credits: credits ? [credits] : [],
      organization_autorecharge: autorecharge ? [autorecharge] : [],
    };
  }
  return hasura(GetOrganizationCredits, { org_id: orgId });
}

// Live bots of the org, oldest first (GetOrganizationBotsLight).
export async function fetchOrganizationBotsLight(orgId: string): Promise<Row[] | null> {
  if (isUserStoreRest()) return getAll(orgPath(orgId, '/bots'));
  return (await hasura(GetOrganizationBotsLight, { organization_id: orgId }))?.bots ?? null;
}

// The caller's users_organizations rows for that org and bot: [{ rol }].
export async function fetchUserBotRoleRows(userId: string, orgId: string, botId: string): Promise<Row[]> {
  if (isUserStoreRest()) {
    const { memberships = [] } = await getData('me');
    return memberships
      .filter((m: Row) => m.organization_id === orgId && m.bot_id === botId)
      .map((m: Row) => ({ rol: m.role }));
  }
  const data = await hasura(GetUserBotRole, { user_id: userId, organization_id: orgId, bot_id: botId });
  return data?.users_organizations || [];
}

// Non-demo sessions of the bot created inside the billing cycle.
export async function countBotSessionsInCycle(botId: string, start: string, end: string): Promise<number> {
  if (isUserStoreRest()) {
    const params = { datetime_from: start, datetime_to: end, granularity: 'day' };
    return Number((await getData(`sessions/stats/${encodeURIComponent(botId)}`, params)).total_sessions ?? 0);
  }
  const vars = { bot_id: botId, start_date: start, end_date: end };
  const data = await hasura(GetBotSessionsInBillingCycle, vars);
  return data?.bot_sessions_aggregate?.aggregate?.count ?? 0;
}
